<template>
    <div class="login-container">
      <div class="login-card">
        <Card>
          <template #title>
            <div class="flex align-items-center justify-content-center mb-3">
              <i class="pi pi-bolt text-primary mr-2" style="font-size: 2rem"></i>
              <h1 class="text-center m-0">Energy Dashboard</h1>
            </div>
          </template>
          <template #subtitle>
            <h2 class="text-center text-600 m-0">Connexion</h2>
          </template>
          <template #content>
            <form @submit.prevent="handleSubmit" class="p-fluid">
              <div class="field">
                <label for="username" class="font-bold">Nom d'utilisateur</label>
                <InputText
                  id="username"
                  v-model="username"
                  :class="{ 'p-invalid': usernameError }"
                  placeholder="Entrez votre nom d'utilisateur"
                  required
                />
                <small v-if="usernameError" class="p-error">{{ usernameError }}</small>
              </div>
              
              <div class="field">
                <label for="password" class="font-bold">Mot de passe</label>
                <Password
                  id="password"
                  v-model="password"
                  :class="{ 'p-invalid': passwordError }"
                  :feedback="false"
                  toggleMask
                  placeholder="Entrez votre mot de passe"
                  required
                />
                <small v-if="passwordError" class="p-error">{{ passwordError }}</small>
              </div>
              
              <div class="field-checkbox">
                <Checkbox id="rememberMe" v-model="rememberMe" :binary="true" />
                <label for="rememberMe">Se souvenir de moi</label>
              </div>
              
              <Message v-if="loginError" severity="error" :closable="false">{{ loginError }}</Message>
              
              <div class="flex flex-column">
                <Button
                  type="submit"
                  label="Connexion"
                  icon="pi pi-sign-in"
                  class="mt-2"
                  :loading="loading"
                />
                
                <Divider align="center" type="solid">
                  <span class="text-600 font-normal text-sm">OU</span>
                </Divider>
                
                <Button
                  type="button"
                  label="Créer un compte"
                  icon="pi pi-user-plus"
                  class="p-button-outlined"
                  @click="goToRegister"
                />
              </div>
            </form>
          </template>
        </Card>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useToast } from 'primevue/usetoast'
  import api from '@/service/api'
  const router = useRouter()
  const toast = useToast()
  
  // Form data
  const username = ref('')
  const password = ref('')
  const rememberMe = ref(false)
  const loading = ref(false)
  
  // Form errors
  const usernameError = ref('')
  const passwordError = ref('')
  const loginError = ref('')
  
  const validateForm = () => {
    let isValid = true
    
    usernameError.value = ''
    passwordError.value = ''
    loginError.value = ''
    
    if (!username.value.trim()) {
      usernameError.value = 'Le nom d\'utilisateur est requis'
      isValid = false
    }
    
    if (!password.value) {
      passwordError.value = 'Le mot de passe est requis'
      isValid = false
    }
    
    return isValid
  }
  
  const handleSubmit = async () => {
    if (!validateForm()) return
    
    loading.value = true
    loginError.value = ''
    
    try {
      const response = await api.post('/login/', {
        email: username.value,
        password: password.value
      })
      
      // Store authentication token
      localStorage.setItem('token', response.data.token)
      
      // Store user data if available
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user))
      }
      
      toast.add({
        severity: 'success',
        summary: 'Connecté',
        detail: 'Connexion réussie!',
        life: 3000
      })
      
      // Redirect to dashboard
      router.push({ name: 'Dashboard' })
    } catch (error) {
      console.error('Login error:', error)
      
      if (error.response) {
        // Handle different error responses
        if (error.response.status === 400) {
          if (error.response.data.non_field_errors) {
            loginError.value = error.response.data.non_field_errors[0]
          } else if (error.response.data.detail) {
            loginError.value = error.response.data.detail
          } else {
            loginError.value = 'Identifiants invalides. Veuillez réessayer.'
          }
        } else {
          loginError.value = 'Une erreur est survenue lors de la connexion. Veuillez réessayer.'
        }
      } else {
        loginError.value = 'Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.'
      }
    } finally {
      loading.value = false
    }
  }
  
  const goToRegister = () => {
    router.push({ name: 'Register' })
  }
  </script>
  
  <style lang="scss" scoped>
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
    padding: 2rem;
  }
  
  .login-card {
    width: 100%;
    max-width: 450px;
    
    :deep(.p-card) {
      border-radius: 1rem;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      
      .p-card-title {
        font-size: 1.5rem;
      }
    }
  }
  
  .field-checkbox {
    margin: 1rem 0;
    display: flex;
    align-items: center;
    
    label {
      margin-left: 0.5rem;
      font-size: 0.9rem;
      color: var(--text-color-secondary);
    }
  }
  </style>